/**
 * Course-home: the authenticated candidate's primary destination.
 *
 * Renders the 10-module list with per-module unlock state, lesson
 * progress, and the right next-step CTA for each. Below the modules:
 * Final Exam and Capstone cards (with appropriate locked/unlocked
 * messaging). Above: the welcome headline + overall progress.
 *
 * One API call: GET /api/courses/cait-foundations/progress (Sprint 6).
 *
 * Behavior:
 *   - 401 → redirect to WorkOS login with return_to=/course
 *   - 200 → render the module list + exam + capstone + cert
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { API_BASE_URL, apiGet, apiPost, ApiError } from "@/lib/api";

const COURSE_SLUG = "cait-foundations";

interface LessonProgress {
  sequence: number;
  slug: string;
  title: string;
  completed: boolean;
}

interface ModuleProgress {
  sequence: number;
  slug: string;
  title: string;
  lessons: LessonProgress[];
  lessons_completed: number;
  lessons_total: number;
  quiz_passed: boolean;
  quiz_attempts: number;
  unlocked: boolean;
  blocking_reason: string | null;
  next_lesson_slug: string | null;
}

interface CourseProgress {
  course_slug: string;
  course_title: string;
  /** "active" | "pending" | "none" — see backend modules.py docstring. */
  enrollment_status: string;
  modules: ModuleProgress[];
  exam: {
    unlocked: boolean;
    passed: boolean;
    attempts: number;
    blocking_reason: string | null;
    /** Latest graded attempt's overall percentage. Null if no attempt
     *  has been graded yet. */
    latest_score?: number | null;
    /** Per-domain percentage breakdown for the latest graded attempt.
     *  Null on pre-v0014 rows (no backfill) — frontend falls back to
     *  the overall score in that case. */
    latest_domain_scores?: Record<string, number> | null;
    /** Per-domain pass-floor snapshot from the latest attempt
     *  (e.g. ``{technical: 60, ...}``). Pair with latest_domain_scores
     *  to render score/floor tiles like the post-submit ExamPage card. */
    latest_domain_floor?: Record<string, number> | null;
  };
  capstone: {
    unlocked: boolean;
    submitted: boolean;
    graded: boolean;
    passed: boolean;
    blocking_reason: string | null;
  };
  certificate: {
    issued: boolean;
    credential_id: string | null;
    verify_url: string | null;
  };
}

interface AuthMeResponse {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "authed"; user: AuthMeResponse; progress: CourseProgress }
  | { kind: "error"; message: string };

/**
 * Guard against an auth-redirect loop where:
 *   1. Callback redirects /course?session=… and sets the wos_session cookie
 *   2. SPA mounts, calls /api/auth/me, the cookie isn't yet sent (a known
 *      cross-origin race on first XHR after a 302), backend returns 401
 *   3. SPA does window.location.href = .../auth/login, which round-trips
 *      WorkOS → AuthKit refresh-token. If this loops fast enough the
 *      per-(email|challenge_id) signup limit (10/60s) saturates and
 *      AuthKit surfaces a "too_many_requests" page with no recovery.
 *
 * The fix: track the time of the last login redirect in sessionStorage.
 * If we're attempting another within 5 seconds, we're in the loop —
 * fail closed (show the error UI) instead of looping again.
 */
const LAST_AUTH_REDIRECT_KEY = "_caitAuthRedirectAt";

function redirectToLogin(returnTo: string = "/course"): void {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(LAST_AUTH_REDIRECT_KEY) ?? "0");
    if (now - last < 5_000) {
      // Loop guard tripped — don't redirect again. The caller's catch
      // path will surface an error instead.
      throw new Error(
        "Sign-in interrupted. Refresh the page or open Course home from the menu.",
      );
    }
    sessionStorage.setItem(LAST_AUTH_REDIRECT_KEY, String(now));
  } catch (e) {
    if (e instanceof Error && /Sign-in interrupted/.test(e.message)) {
      throw e;
    }
    /* sessionStorage may be unavailable — fall through and redirect once */
  }
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

interface CourseHomeProps {
  onUnauthenticated?: (returnTo: string) => void;
}

function ModuleCard({ m }: { m: ModuleProgress }): React.JSX.Element {
  const allLessonsDone = m.lessons_completed === m.lessons_total && m.lessons_total > 0;
  const showRetakeQuizCTA = m.quiz_passed;
  // Pick the headline status badge.
  let badge: { label: string; tone: "done" | "active" | "locked" };
  if (!m.unlocked) {
    badge = { label: "Locked", tone: "locked" };
  } else if (m.quiz_passed) {
    badge = { label: "Module complete", tone: "done" };
  } else if (m.lessons_completed > 0 || m.quiz_attempts > 0) {
    badge = { label: "In progress", tone: "active" };
  } else {
    badge = { label: "Unlocked", tone: "active" };
  }

  // Pick the primary CTA.
  let primaryHref: string | null = null;
  let primaryLabel: string | null = null;
  if (m.unlocked) {
    if (!allLessonsDone && m.next_lesson_slug) {
      const verb =
        m.lessons_completed === 0 ? "Start lesson" : `Continue · Lesson ${m.lessons_completed + 1}`;
      primaryHref = `/lesson/${m.slug}/${m.next_lesson_slug}`;
      primaryLabel = verb;
    } else if (allLessonsDone && !m.quiz_passed) {
      primaryHref = `/module/${m.slug}/quiz`;
      primaryLabel = `Take Module ${m.sequence} quiz →`;
    } else if (m.quiz_passed) {
      // All done — let the user revisit any lesson; default to lesson 1.
      primaryHref = `/lesson/${m.slug}/${m.lessons[0]?.slug ?? ""}`;
      primaryLabel = "Re-read lessons";
    }
  }

  // Lesson progress as a percentage (0-100) for the visual bar.
  const lessonPct =
    m.lessons_total > 0
      ? Math.round((m.lessons_completed / m.lessons_total) * 100)
      : 0;

  return (
    <article
      className={[
        "group relative overflow-hidden rounded-xl border p-6 transition-all",
        badge.tone === "done"
          ? "border-emerald-200 bg-emerald-50/60"
          : badge.tone === "active"
            ? "border-border bg-card hover:border-ca-purple/40 hover:shadow-card"
            : "border-border bg-muted/30",
      ].join(" ")}
    >
      <div className="flex items-start gap-5">
        {/* Numbered module badge — gradient for unlocked, muted for locked.
         *  The badge is the per-card brand surface: the only place per
         *  module the gradient appears. Keeps the brand presence steady
         *  through the module list without becoming wallpaper. */}
        <div
          className={[
            "flex h-14 w-14 shrink-0 items-center justify-center rounded-lg font-display text-2xl font-semibold shadow-soft",
            badge.tone === "done"
              ? "bg-emerald-500 text-white"
              : badge.tone === "active"
                ? "bg-ca-gradient text-white"
                : "bg-muted text-muted-foreground",
          ].join(" ")}
          aria-hidden="true"
        >
          {m.sequence}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Module {m.sequence}
            </p>
            <span
              className={[
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                badge.tone === "done"
                  ? "bg-emerald-100 text-emerald-900"
                  : badge.tone === "active"
                    ? "bg-ca-indigo/10 text-ca-indigo"
                    : "bg-muted text-muted-foreground",
              ].join(" ")}
            >
              {badge.tone === "done" ? "✓ " : badge.tone === "locked" ? "🔒 " : ""}
              {badge.label}
            </span>
          </div>
          <h2 className="mt-1 text-xl font-semibold text-foreground">{m.title}</h2>

          {/* Progress bar — visible on every card so learners see motion
           *  through the module list, even on locked modules (which show
           *  an empty track as a hint that there's progress to be made). */}
          <div className="mt-3 flex items-center gap-3">
            <div
              className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={lessonPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Lessons: ${m.lessons_completed} of ${m.lessons_total}`}
            >
              <div
                className={[
                  "h-full transition-all",
                  badge.tone === "done"
                    ? "bg-emerald-500"
                    : badge.tone === "active"
                      ? "bg-ca-gradient"
                      : "bg-transparent",
                ].join(" ")}
                style={{ width: `${lessonPct}%` }}
              />
            </div>
            <p className="shrink-0 text-xs text-muted-foreground">
              {m.lessons_completed}/{m.lessons_total} lessons
              {m.quiz_passed
                ? " · quiz passed"
                : m.quiz_attempts > 0
                  ? ` · quiz ×${m.quiz_attempts}`
                  : ""}
            </p>
          </div>

          {/* Locked-state lesson preview — show titles dimmed instead of
           *  hiding entirely so learners get a sense of what's ahead.
           *  Encourages continued progress through the gating module.
           *  Optional-chained: the backend may omit the lessons array
           *  on locked modules; treat missing as zero-length. */}
          {!m.unlocked && (m.lessons?.length ?? 0) > 0 ? (
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground/70">
              {m.lessons.slice(0, 3).map((l) => (
                <li key={l.slug} className="flex items-center gap-2">
                  <span className="text-muted-foreground/40">·</span>
                  {l.title}
                </li>
              ))}
              {m.lessons.length > 3 ? (
                <li className="text-muted-foreground/50">
                  + {m.lessons.length - 3} more lessons
                </li>
              ) : null}
            </ul>
          ) : null}

          {!m.unlocked && m.blocking_reason ? (
            <p className="mt-3 text-sm text-muted-foreground">{m.blocking_reason}</p>
          ) : null}

          {primaryHref && primaryLabel ? (
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to={primaryHref}
                className={[
                  "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all",
                  badge.tone === "done"
                    ? "border border-emerald-700 bg-white text-emerald-900 hover:bg-emerald-100"
                    : "bg-ca-gradient text-white shadow-soft hover:shadow-card hover:-translate-y-px",
                  "focus:outline-none focus:ring-2 focus:ring-ca-indigo focus:ring-offset-2",
                ].join(" ")}
              >
                {primaryLabel}
              </Link>
              {showRetakeQuizCTA ? (
                <Link
                  to={`/module/${m.slug}/quiz`}
                  className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-semibold text-foreground hover:border-ca-purple/40 hover:bg-ca-gradient-subtle focus:outline-none focus:ring-2 focus:ring-ca-indigo focus:ring-offset-2"
                >
                  Re-take quiz
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/**
 * HeroStrip — "Continue learning" panel that pins the right next-step
 * CTA at the top of /course. The most common returning-learner action
 * is "where was I?" — this answers it in one glance.
 *
 * Layout: left side is the user-facing copy + the big CTA. Right side
 * is a circular progress ring showing modules complete out of total.
 * On mobile the ring drops below the text.
 *
 * Computes `next` from the modules list: first unlocked module that
 * isn't fully passed yet. If everything's done, no hero is shown
 * (we render the "you're certified" emerald card instead, further
 * down the page).
 */
function HeroStrip({
  firstName,
  progress,
}: {
  firstName: string;
  progress: CourseProgress;
}): React.JSX.Element | null {
  const modules = progress.modules ?? [];
  const totalModules = modules.length;
  const modulesPassed = modules.filter((m) => m.quiz_passed).length;
  // Find the next actionable module — first unlocked + not-fully-passed.
  const nextModule = modules.find(
    (m) => m.unlocked && (!m.quiz_passed || m.lessons_completed < m.lessons_total),
  );

  if (!nextModule) return null;

  // Decide the deep-link target + label.
  let ctaHref: string;
  let ctaLabel: string;
  if (nextModule.next_lesson_slug) {
    ctaHref = `/lesson/${nextModule.slug}/${nextModule.next_lesson_slug}`;
    const lessonNumber = nextModule.lessons_completed + 1;
    ctaLabel =
      nextModule.lessons_completed === 0
        ? `Start Module ${nextModule.sequence}`
        : `Continue · Lesson ${lessonNumber}`;
  } else {
    ctaHref = `/module/${nextModule.slug}/quiz`;
    ctaLabel = `Take Module ${nextModule.sequence} quiz`;
  }

  // Progress ring math — SVG arc length proportional to modules passed.
  const ringSize = 96;
  const stroke = 8;
  const radius = (ringSize - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const pctComplete = totalModules > 0 ? modulesPassed / totalModules : 0;
  const dashOffset = circumference * (1 - pctComplete);

  return (
    <section
      className="relative mt-8 overflow-hidden rounded-2xl border border-border bg-ca-gradient-subtle p-6 sm:p-8"
      aria-label="Continue learning"
    >
      {/* Subtle gradient accent rail along the left edge */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-ca-gradient" aria-hidden="true" />

      <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-ca-indigo">
            {firstName}, pick up where you left off
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-foreground sm:text-3xl">
            Module {nextModule.sequence}: {nextModule.title}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {modulesPassed} of {totalModules} modules complete · {nextModule.lessons_completed} of{" "}
            {nextModule.lessons_total} lessons in this module
          </p>
          <div className="mt-5">
            <Link
              to={ctaHref}
              className="inline-flex items-center gap-2 rounded-lg bg-ca-gradient px-6 py-3 text-base font-semibold text-white shadow-card transition-all hover:-translate-y-px hover:shadow-hover focus:outline-none focus:ring-2 focus:ring-ca-indigo focus:ring-offset-2"
            >
              {ctaLabel} →
            </Link>
          </div>
        </div>

        {/* Progress ring — modules passed out of total. The brand
         *  gradient is rendered as an SVG gradient on the stroke. */}
        <div className="relative" aria-hidden="true">
          <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
            <defs>
              <linearGradient id="hero-ring" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="hsl(230 50% 36%)" />
                <stop offset="35%" stopColor="hsl(263 84% 58%)" />
                <stop offset="70%" stopColor="hsl(330 81% 60%)" />
                <stop offset="100%" stopColor="hsl(25 95% 61%)" />
              </linearGradient>
            </defs>
            <circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={radius}
              fill="none"
              stroke="hsl(var(--border))"
              strokeWidth={stroke}
            />
            <circle
              cx={ringSize / 2}
              cy={ringSize / 2}
              r={radius}
              fill="none"
              stroke="url(#hero-ring)"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
              style={{ transition: "stroke-dashoffset 400ms ease" }}
            />
            <text
              x="50%"
              y="48%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-foreground font-display"
              style={{ fontSize: "1.5rem", fontWeight: 600 }}
            >
              {modulesPassed}
            </text>
            <text
              x="50%"
              y="68%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-muted-foreground"
              style={{ fontSize: "0.625rem", letterSpacing: "0.1em" }}
            >
              OF {totalModules}
            </text>
          </svg>
        </div>
      </div>
    </section>
  );
}

function GateCard({
  title,
  description,
  unlocked,
  done,
  doneLabel,
  blockingReason,
  ctaHref,
  ctaLabel,
}: {
  title: string;
  description: string;
  unlocked: boolean;
  done: boolean;
  doneLabel?: string;
  blockingReason: string | null;
  ctaHref: string;
  ctaLabel: string;
}): React.JSX.Element {
  const tone = done ? "done" : unlocked ? "active" : "locked";
  return (
    <article
      className={[
        "rounded-lg border p-6",
        tone === "done"
          ? "border-emerald-200 bg-emerald-50"
          : tone === "active"
            ? "border-border bg-white"
            : "border-border bg-muted/40",
      ].join(" ")}
    >
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </p>
        <span
          className={[
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
            tone === "done"
              ? "bg-emerald-100 text-emerald-900"
              : tone === "active"
                ? "bg-slate-100 text-slate-900"
                : "bg-slate-200 text-slate-700",
          ].join(" ")}
        >
          {tone === "done"
            ? `✓ ${doneLabel ?? "Done"}`
            : tone === "locked"
              ? "🔒 Locked"
              : "Unlocked"}
        </span>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">{description}</p>
      {!unlocked && blockingReason ? (
        <p className="mt-3 text-sm text-muted-foreground">{blockingReason}</p>
      ) : null}
      {unlocked && !done ? (
        <div className="mt-5">
          <Link
            to={ctaHref}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {ctaLabel}
          </Link>
        </div>
      ) : null}
    </article>
  );
}

/** Persistent per-attempt exam result card shown on the course-home
 *  page once the candidate has at least one graded attempt. Mirrors
 *  the visual language of the post-submit ExamPage card so a returning
 *  student sees the same shape they saw at submit time. v0014 / Bug 3.1
 *  follow-up: persistent because /progress now returns latest_score +
 *  latest_domain_scores + latest_domain_floor from the most recent
 *  graded attempt. */
function ExamLatestResult({
  score,
  passed,
  domainScores,
  domainFloor,
}: {
  score: number;
  passed: boolean;
  domainScores: Record<string, number> | null;
  domainFloor: Record<string, number> | null;
}): React.JSX.Element {
  return (
    <article
      className={[
        "rounded-lg border p-5",
        passed
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50",
      ].join(" ")}
      aria-label="Latest exam result"
    >
      <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
        {passed ? "Latest exam — passed" : "Latest exam — not yet passed"}
      </h3>
      <p className="mt-1 text-2xl font-bold text-foreground">
        {score}%
      </p>
      {domainScores ? (
        <>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Per-domain breakdown
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {Object.entries(domainScores).map(([domain, pct]) => {
              const floor = domainFloor?.[domain] ?? 0;
              const met = pct >= floor;
              return (
                <div
                  key={domain}
                  className={[
                    "rounded-md border p-2 text-xs",
                    met
                      ? "border-border bg-white"
                      : "border-red-200 bg-red-50",
                  ].join(" ")}
                >
                  <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                    {domain}
                  </dt>
                  <dd className="mt-1 font-mono text-base font-semibold text-foreground">
                    {pct}%
                  </dd>
                  <dd className="text-[10px] text-muted-foreground">
                    floor: {floor}%
                  </dd>
                </div>
              );
            })}
          </dl>
        </>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">
          Per-domain breakdown not available for this attempt.
        </p>
      )}
    </article>
  );
}

export function CourseHome({
  onUnauthenticated = redirectToLogin,
}: CourseHomeProps = {}): React.JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const navigate = useNavigate();

  const onSignOut = React.useCallback(async () => {
    try {
      await apiPost("/api/auth/logout");
    } catch {
      /* best-effort */
    }
    void navigate("/");
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const user = await apiGet<AuthMeResponse>("/api/auth/me");
        const progress = await apiGet<CourseProgress>(
          `/api/courses/${COURSE_SLUG}/progress`,
        );
        if (cancelled) return;
        setState({ kind: "authed", user, progress });
      } catch (err: unknown) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          try {
            onUnauthenticated("/course");
          } catch (loopErr) {
            // Loop guard tripped — surface as an error so the user
            // can recover instead of looping.
            const message =
              loopErr instanceof Error
                ? loopErr.message
                : "Sign-in interrupted.";
            setState({ kind: "error", message });
          }
          return;
        }
        const message =
          err instanceof Error ? err.message : "Failed to load your course progress.";
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onUnauthenticated]);

  if (state.kind === "loading") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24" aria-busy="true">
        <p className="text-sm text-muted-foreground" role="status">
          Loading your course home…
        </p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
        <p className="mt-4 text-sm text-foreground" role="alert">
          {state.message}
        </p>
        <p className="mt-6 text-sm text-muted-foreground">
          Try refreshing the page. If the problem persists,{" "}
          <a
            className="font-semibold text-foreground underline"
            href="mailto:hello@cait.clinicianassist.ai"
          >
            email support
          </a>
          .
        </p>
      </section>
    );
  }

  const { user, progress } = state;
  // Fall through to email when name is null OR an empty/whitespace
  // string — `?? user.email` alone misses the empty-string case and
  // produces "Welcome, ." in the heading.
  const trimmedFirst = user.name?.trim().split(/\s+/)[0];
  const firstName = trimmedFirst && trimmedFirst.length > 0 ? trimmedFirst : user.email;
  // Defensive: backend response shape can vary by enrollment state and
  // historical responses. Coerce every nested object to a safe default
  // so this render path never throws on a missing field — the only
  // place the missing-field state degrades is visual (counts read as
  // zero) rather than tearing down the whole React tree.
  const modules = progress.modules ?? [];
  const totalModules = modules.length;
  const modulesPassed = modules.filter((m) => m.quiz_passed).length;
  const certificate = progress.certificate ?? {
    issued: false,
    credential_id: null,
    verify_url: null,
  };
  const exam = progress.exam ?? {
    unlocked: false,
    passed: false,
    attempts: 0,
    blocking_reason: null,
    latest_score: null,
    latest_domain_scores: null,
    latest_domain_floor: null,
  };
  const capstone = progress.capstone ?? {
    unlocked: false,
    submitted: false,
    graded: false,
    passed: false,
    blocking_reason: null,
  };

  // No enrollment → CaiT Foundations is invitation-only. Render the
  // dedicated "request an invitation" page instead of the module list
  // (which would lie by showing M1 as Unlocked + Start lesson — the
  // backend correctly 403s on the actual lesson fetch).
  if (progress.enrollment_status === "none") {
    return (
      <section className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24">
        <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {progress.course_title}
        </p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
          Hi {firstName} — this program is invitation-only.
        </h1>
        <p className="mt-6 text-lg text-foreground">
          The CaiT Foundations Certificate is currently limited to clinicians
          in our Founding Cohort. Your account was created successfully, but
          you don't have an active enrollment yet.
        </p>
        <p className="mt-4 text-base text-muted-foreground">
          Request an invitation through the program page, and we'll be in
          touch. If you were already invited and believe this is a mistake,
          make sure you signed up with the same email address that received
          your invitation.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="https://clinicianassist.ai/cait"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-base font-semibold text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Request an invitation →
          </a>
          <button
            type="button"
            onClick={() => {
              void onSignOut();
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-5 py-3 text-base font-semibold text-foreground hover:bg-muted/40"
          >
            Sign out
          </button>
        </div>
        <p className="mt-10 text-xs text-muted-foreground">
          Signed in as <span className="font-semibold">{user.email}</span>.
        </p>
      </section>
    );
  }

  // Pending enrollment (mid-activation, rare) — small notice; the
  // activation hook should flip this to active on the same callback,
  // but if a candidate gets here we tell them what's happening.
  if (progress.enrollment_status === "pending") {
    return (
      <section className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24">
        <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {progress.course_title}
        </p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground">
          Activating your enrollment…
        </h1>
        <p className="mt-6 text-base text-foreground">
          We're flipping your enrollment from pending to active. This usually
          happens automatically on first sign-in. Refresh in a moment — if
          this notice doesn't go away,{" "}
          <a className="underline" href="mailto:hello@cait.clinicianassist.ai">
            email support
          </a>
          .
        </p>
        <p className="mt-10 text-xs text-muted-foreground">
          Signed in as <span className="font-semibold">{user.email}</span>.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        {progress.course_title} · Founding Cohort
      </p>
      <h1 className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
        Welcome, {firstName}.
      </h1>
      <p className="mt-6 text-lg text-foreground">
        {certificate.issued
          ? "You're certified. Modules below stay open so you can revisit any time."
          : `Module ${modulesPassed} of ${totalModules} complete. New modules unlock as you pass each module's quiz at 80%+.`}
      </p>

      {/* "Continue learning" hero — pins the next action above the module
       *  list so a returning learner answers "where was I?" in one glance.
       *  Self-hides when there's nothing actionable (everything passed). */}
      <HeroStrip firstName={firstName} progress={progress} />

      {certificate.issued && certificate.verify_url ? (
        <article className="mt-8 rounded-lg border border-emerald-300 bg-emerald-50 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-900">
            ✓ Certificate issued
          </p>
          <h2 className="mt-2 text-xl font-semibold text-foreground">
            CaiT Foundations Certificate
          </h2>
          <p className="mt-2 text-sm text-emerald-900">
            Public verification:{" "}
            <a
              className="font-semibold underline"
              href={certificate.verify_url}
              target="_blank"
              rel="noreferrer"
            >
              {certificate.verify_url}
            </a>
          </p>
        </article>
      ) : null}

      <h2 className="mt-12 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Modules
      </h2>
      <div className="mt-4 space-y-4">
        {modules.map((m) => (
          <ModuleCard key={m.sequence} m={m} />
        ))}
      </div>

      <h2 className="mt-12 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Credential
      </h2>
      <div className="mt-4 space-y-4">
        <GateCard
          title="Final exam"
          description="50-item stratified exam across all module learning objectives. 80% to pass. 3 attempts per 24-hour cycle."
          unlocked={exam.unlocked}
          done={exam.passed}
          doneLabel="Exam passed"
          blockingReason={exam.blocking_reason}
          ctaHref="/exam/cait-foundations"
          ctaLabel={
            exam.attempts > 0
              ? `Resume final exam (attempt ${exam.attempts + 1})`
              : "Start final exam →"
          }
        />
        {/* v0014 (FIX-PLAN follow-up): persistent per-domain breakdown
         *  for students who navigated away from the post-submit results
         *  page. Shows the latest graded attempt's overall score and
         *  per-domain tiles. Renders only when the user has at least
         *  one graded attempt (latest_score != null). Falls back to
         *  overall-only when latest_domain_scores is null (pre-v0014
         *  attempts with no backfill).
         */}
        {exam.latest_score != null ? (
          <ExamLatestResult
            score={exam.latest_score}
            passed={exam.passed}
            domainScores={exam.latest_domain_scores ?? null}
            domainFloor={exam.latest_domain_floor ?? null}
          />
        ) : null}
        <GateCard
          title="Capstone"
          description="Open-response essay graded by the CaiT Foundations AI grader (Claude Sonnet); borderline outcomes queued for human review."
          unlocked={capstone.unlocked}
          done={capstone.passed}
          doneLabel="Capstone passed"
          blockingReason={capstone.blocking_reason}
          ctaHref="/capstone/cait-foundations"
          ctaLabel={
            capstone.submitted ? "View submission" : "Submit capstone →"
          }
        />
      </div>

      <div className="mt-12 rounded-lg border border-border bg-background p-4 text-xs text-muted-foreground">
        <p>
          Signed in as <span className="font-semibold">{user.name?.trim() || user.email}</span>
          {user.role === "admin" ? " · admin" : ""}.{" "}
          <button
            type="button"
            onClick={() => {
              void onSignOut();
            }}
            className="font-semibold text-foreground underline hover:text-foreground"
          >
            Sign out
          </button>
        </p>
      </div>
    </section>
  );
}
